
export enum Features
{
    // Mirrors the backend's Features enum. Bit 1 is named DRIVE here, ACCDRIVE there;
    // the value is what matters. AMS2 and RR exist as prototypes only — they are granted
    // (a package covers them at launch) but kept out of customer-facing lists.
    Base = 0,
    DRIVE = 1,
    ACADEMY = 2,
    IRDRIVE = 4,
    ACEDRIVE = 8,
    ACDRIVE = 16,
    AMS2DRIVE = 32,
    LMUDRIVE = 64,
    RRDRIVE = 128,
    ACTV = 256,
    LMUTV = 512,
    LMUTVPRO = 1024
}
const FeatureNames: Record<Features, string> = {
  [Features.Base]: "Base", // Wird nicht genutzt
  [Features.DRIVE]: "ACC DRIVE",
  [Features.ACADEMY]: "ACC Academy",
  [Features.IRDRIVE]: "iR DRIVE",
  [Features.ACEDRIVE]: "ACE DRIVE",
  [Features.ACDRIVE]: "AC DRIVE",
  [Features.AMS2DRIVE]: "AMS2 DRIVE",
  [Features.LMUDRIVE]: "LMU DRIVE",
  [Features.RRDRIVE]: "RR DRIVE",
  [Features.ACTV]: "ACC TV",
  [Features.LMUTV]: "LMU TV",
  [Features.LMUTVPRO]: "LMU TV Pro"
};
export const hasFlag = (role: Features, flag: Features): boolean => {
  return (role & flag) === flag;
};

export const listApps = (flag: Features): string[] => {
  return Object.entries(Features)
      .filter(([key, value]) => 
          typeof value === "number" && value !== Features.Base && (flag & (value as Features)) !== 0
      )
      .map(([key]) => key);
};

export const ListAppsNames = (flag: Features): string[] => {
  return Object.entries(Features)
      .filter(([key, value]) => 
          typeof value === "number" && value !== Features.Base && (flag & (value as Features)) !== 0
      )
      .map(([_, value]) => FeatureNames[value as Features] || "Unknown");
};

/**
 * Timestamps from the Key API arrive without a zone ("2026-12-10T23:59:59.99") but are
 * UTC. new Date() would read them as local time and show them hours off, so the marker
 * is added when it is missing; everything downstream then formats in the viewer's zone.
 */
export const parseApiDate = (raw: string): Date =>
  new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(raw) ? raw : raw + 'Z');

/**
 * The apps a customer can actually download today. A Welcome Package grants every
 * drive-side bit including AMS2 and RR DRIVE, which are prototypes — listing those by
 * name only produces "when do I get it?" tickets, so UI labels go through here.
 */
export const RELEASED_APPS: Features =
  Features.DRIVE | Features.ACADEMY | Features.IRDRIVE |
  Features.ACEDRIVE | Features.ACDRIVE | Features.LMUDRIVE;

/** Like ListAppsNames, minus the unreleased apps. Use this for anything a buyer sees. */
export const ListReleasedAppNames = (flag: Features): string[] =>
  ListAppsNames(flag & RELEASED_APPS);

export interface FeatureGrantLike {
  features: number;
  validUntil?: string | null;
  type?: string;
}

export interface KeyLike {
  features: number;
  refunded?: boolean;
  validUntil?: string | null;
  packageValidUntil?: string | null;
  featureGrants?: FeatureGrantLike[];
}

/**
 * Every app bit a key carries — permanent base features OR every feature grant.
 * A Welcome Package key has features 0 and holds everything in its grant, so reading
 * `features` alone hides it from the account list.
 */
export const allFeatures = (key: KeyLike): Features => {
  let mask = key.features ?? 0;
  for (const grant of key.featureGrants ?? []) mask |= grant.features ?? 0;
  return mask;
};

/**
 * The bits the key unlocks right now: expired base features and lapsed grants drop out,
 * a refunded key unlocks nothing. Mirrors the backend's GetEffectiveFeatures.
 */
export const activeFeatures = (key: KeyLike, now: number = Date.now()): Features => {
  const baseAlive = !key.refunded && (!key.validUntil || new Date(key.validUntil).getTime() > now);
  let mask = baseAlive ? (key.features ?? 0) : 0;
  if (key.refunded) return mask;
  for (const grant of key.featureGrants ?? []) {
    if (!grant.validUntil || new Date(grant.validUntil).getTime() > now) mask |= grant.features ?? 0;
  }
  return mask;
};

/** End of the paid package on this key, past or future, or null when there is none. */
export const packageEnd = (key: KeyLike): Date | null => {
  const raw = key.packageValidUntil
    ?? key.featureGrants?.filter(g => g.type !== 'Subscription' && g.validUntil)
         .map(g => g.validUntil as string).sort().pop();
  return raw ? parseApiDate(raw) : null;
};

/** Package end only while it is still running. */
export const runningPackageEnd = (key: KeyLike, now: number = Date.now()): Date | null => {
  const end = packageEnd(key);
  return end && end.getTime() > now ? end : null;
};
